import {
  OrganizationResponseForAdmins,
  OrganizationsService,
} from "client/openapi";
import { Dialog, DialogContent } from "components/Dialog";
import InviteParentDialog from "components/Dashboard/InviteParentDialog";
import InviteStudentDialog from "components/Dashboard/InviteStudentDialog";
import InviteTutorDialog from "components/Dashboard/InviteTutorDialog";
import { useEffect, useState } from "react";
import UnknownContactDialog, {
  UnknownContactInfo,
  UserType,
} from "./UnknownContactDialog";
import "./index.css";

/**
 * A sender who matches no one: link their number or email to a student,
 * parent or tutor, or add a new one through today's Add dialog (with its
 * form picker), filled in with the number or email. Open while `contact` is
 * set.
 */
export default function UnknownSenderDialog({
  contact,
  orgId,
  onChanged,
  onClose,
}: {
  contact: UnknownContactInfo | null;
  orgId: number;
  /** Someone now has this number or email (linked or added). */
  onChanged: () => void;
  onClose: () => void;
}) {
  const [inviteAs, setInviteAs] = useState<UserType | null>(null);
  const [org, setOrg] = useState<OrganizationResponseForAdmins>();

  // The organization's people, fresh each time the dialog opens.
  useEffect(() => {
    setOrg(undefined);
    if (!contact) return;
    let active = true;
    OrganizationsService.getOrganization({ orgId })
      .then((o) => {
        if (active) setOrg(o as OrganizationResponseForAdmins);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [contact, orgId]);

  function close() {
    setInviteAs(null);
    onClose();
  }

  const added = async () => onChanged();
  const prefill = {
    initialEmail: contact?.email ?? "",
    initialPhone: contact?.phone_number ?? "",
  };

  return (
    <Dialog open={!!contact} onOpenChange={(open) => !open && close()}>
      {contact && inviteAs === "parent" && (
        <InviteParentDialog fetchParents={added} currentPage={0} {...prefill} />
      )}
      {contact && inviteAs === "student" && (
        <InviteStudentDialog
          fetchStudents={added}
          currentPage={0}
          orgId={orgId}
          {...prefill}
        />
      )}
      {contact && inviteAs === "tutor" && (
        <InviteTutorDialog fetchTutors={added} currentPage={0} {...prefill} />
      )}
      {contact && !inviteAs && (
        <DialogContent className="link-unknown-contact-dialog-content">
          {org ? (
            <UnknownContactDialog
              inboundContactInfo={contact}
              organization={org}
              onCreate={setInviteAs}
              onClose={() => {
                onChanged();
                close();
              }}
            />
          ) : (
            <div className="mt-10 animate-pulse space-y-3">
              <div className="h-16 rounded-lg bg-gray-200" />
              <div className="h-6 w-2/3 rounded bg-gray-100" />
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
